import React, { useEffect, useMemo, useState } from 'react';
import { Col, Form, Modal, Row, Tabs, TabsProps } from 'antd';
import { isEqual } from 'lodash';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { clearFieldErrors, handleFormErrors } from '../../common/errorHelpers';
import { getEnumKeyByEnumValue } from '../../common/helpers';
import { AttachementTypeEnum } from '../../common/types';
import { useAttachFile } from '../../common/useAttachFile';
import { useGetCandidate } from '../../common/useGetCandidate';
import { useHeaderHeight } from '../../common/useHeaderHeight';
import { useTableDataHeight } from '../../common/useTableDataHeight';
import { BreadcrumbItem, Breadcrumbs } from '../../components/Breadcrumbs';
import {
  CandidateAdditionalDataForm,
  CandidateDataForm,
  mapErrorFieldsToFormLabels,
} from '../../components/CandidateDataForm';
import { CandidateDuplicatesProvider } from '../../components/CandidateDuplicatesContext';
import { StyledRow } from '../../components/StyledRow';
import Icon from '../../icons/Icon';
import { PageRoutesEnum } from '../../utils/routes';
import {
  getCandidateAiReviewStateFromCandidate,
  isCandidateAiProcessingCutoffBlocked,
  isCandidateCvProcessingActive,
} from '../candidate-ai/candidateAiReviewState';
import { useGetCandidateAiReviewState } from '../candidate-ai/useGetCandidateAiReviewState';
import { useOpenMatchingPage } from '../candidate-project-matching/useOpenMatchingPage';
import { CandidateConsentsAddIntent, CandidateConsentsTable } from '../consents/CandidateConsentsTable';
import { HRReview } from '../recruitment-details/HRReview';
import { TechnicalReview } from '../recruitment-details/TechnicalReview';

import { shouldStartCandidateAiRefreshOnLoad } from './candidateAiRefreshNavigation';
import { CandidateAttachements } from './CandidateAttachements';
import { CandidateDetailsPageHeader } from './CandidateDetailsPageHeader';
import {
  buildCandidateFormPayload,
  getComparableCandidateFormValues,
  mapCandidateDetailsToFormValues,
} from './candidateFormValues';
import { CandidateNotes } from './CandidateNotes';
import { CandidateRecruitmentsTable } from './CandidateRecruitmentsTable';
import { CandidateRemindersTable } from './CandidateRemindersTable';
import { CandidatesPersonalData } from './CandidatesPersonalData';
import { useCandidateAiRefreshAfterCvUpload } from './useCandidateAiRefreshAfterCvUpload';
import { useCandidateAiReviewStateSource } from './useCandidateAiReviewStateSource';
import { useConsumeOpenConsentsNavigation } from './useConsumeOpenConsentsNavigation';
import { useStartCandidateAiRefreshForActiveProcessing } from './useStartCandidateAiRefreshForActiveProcessing';
import { useStartCandidateAiRefreshOnNavigation } from './useStartCandidateAiRefreshOnNavigation';
import { useUpdateCandidate } from './useUpdateCandidate';

enum EditCandidateTabsEnum {
  AdditionalData = 'addiionalData',
  HrReview = 'hrReview',
  TechnicalReview = 'technicalReview',
  Notes = 'notes',
  Attachements = 'attachements',
  Recruitments = 'recruitments',
  Reminders = 'reminders',
  Consents = 'consents',
}

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
`;

const TabsColumn = styled(Tabs)`
  .ant-tabs-tab-active {
    font-weight: 600;
  }
`;

export const EditCandidatePage: React.FC = () => {
  const { id } = useParams();

  const { data, isLoading, refetch } = useGetCandidate(id);
  const { data: candidateAiReviewState, refetch: refetchCandidateAiReviewState } = useGetCandidateAiReviewState(id);
  const [form] = Form.useForm();
  const watchedFormValues = Form.useWatch([], form);
  const updateCandidate = useUpdateCandidate();
  const location = useLocation();
  const navigate = useNavigate();
  const openMatchingPage = useOpenMatchingPage({ type: 'candidate', uuid: id || '' });
  // Arriving from the CV suggestions page's consent notice opens the consent form.
  const openConsentForm = useConsumeOpenConsentsNavigation();
  const [active, setActive] = useState(() =>
    openConsentForm ? EditCandidateTabsEnum.Consents : EditCandidateTabsEnum.Recruitments
  );
  const [pickFile, setPickFile] = useState(false);

  const [isConsentModalOpen, setIsConsentModalOpen] = useState(false);
  const [baselineFormValues, setBaselineFormValues] = useState<Record<string, unknown> | null>(null);
  const [consentsAddIntent] = useState<CandidateConsentsAddIntent | null>(() =>
    openConsentForm ? { id: Date.now(), consentTypes: ['DATA_PROCESSING_AND_OFFERS'] } : null
  );

  const [chosenAttachement, setChosenAttachement] = useState('');

  const { isAiSuggestionsProcessing: isAiRefreshSessionActive, startCandidateAiRefreshSession } =
    useCandidateAiRefreshAfterCvUpload({
      aiReviewState: candidateAiReviewState,
      refetchAiReviewState: refetchCandidateAiReviewState,
    });

  const { effectiveAiReviewState, candidateWithLatestAiState } = useCandidateAiReviewStateSource({
    candidate: data,
    polledAiReviewState: candidateAiReviewState,
    isAiRefreshSessionActive,
  });

  const shouldStartAiRefreshOnLoad = shouldStartCandidateAiRefreshOnLoad(location.state);

  const comparableFormValues = useMemo(
    () =>
      getComparableCandidateFormValues({
        ...form.getFieldsValue(true),
        ...((watchedFormValues as Record<string, unknown>) ?? {}),
      }),
    [form, watchedFormValues]
  );

  const isAiReviewDirty = useMemo(() => {
    if (!baselineFormValues) {
      return false;
    }

    return !isEqual(comparableFormValues, baselineFormValues);
  }, [baselineFormValues, comparableFormValues]);

  const { attachGeneralFile, attachHrInterviewFile, attachTechnicalInterviewFile } = useAttachFile(data?.uuid ?? '');

  const hrInterviewAttachements = data?.files.filter((file) => file.type === AttachementTypeEnum.HR_INTERVIEW) || [];

  const technicalInterviewAttachements =
    data?.files.filter((file) => file.type === AttachementTypeEnum.TECHNICAL_INTERVIEW) || [];

  const otherAttachements = data?.files.filter((file) => file.type === AttachementTypeEnum.OTHER) || [];

  const isHrReview = data?.hrInterviewAssessment || data?.hrInterviewDate || data?.hrInterviewSummary;

  const isTechnicalReview =
    data?.technicalInterviewAssessment || data?.technicalInterviewDate || data?.technicalInterviewSummary;

  const { headerHeight, headerRef } = useHeaderHeight();

  const { tableHeight } = useTableDataHeight(headerHeight + 20, 20);

  useEffect(() => {
    if (!data) return;

    const mappedValues = mapCandidateDetailsToFormValues(data);
    form.setFieldsValue(mappedValues);
    setBaselineFormValues(getComparableCandidateFormValues(mappedValues));
  }, [data, form]);

  useEffect(() => {
    if (!data?.consents) return;

    const hasValidConsent = data.consents.some(
      (consent) =>
        consent.type === 'CLIENT_DATA_PROCESSING' && (consent.status === 'APPROVED' || consent.status === 'RENEWED')
    );

    if (!hasValidConsent) {
      setIsConsentModalOpen(true);
    }
  }, [data?.consents]);

  const resolvedAiReviewState = useMemo(() => {
    if (!data) {
      return undefined;
    }

    return effectiveAiReviewState ?? getCandidateAiReviewStateFromCandidate(data);
  }, [data, effectiveAiReviewState]);

  const resolvedCandidateWithLatestAiState = useMemo(() => {
    if (candidateWithLatestAiState) {
      return candidateWithLatestAiState;
    }

    if (!data || !resolvedAiReviewState) {
      return data;
    }

    return {
      ...data,
      aiSuggestions: resolvedAiReviewState.aiSuggestions,
      cvProcessing: resolvedAiReviewState.cvProcessing,
    };
  }, [candidateWithLatestAiState, data, resolvedAiReviewState]);

  const isAiConsentBlocked =
    !!resolvedCandidateWithLatestAiState?.cv &&
    resolvedAiReviewState?.cvProcessing.canProcess === false &&
    resolvedAiReviewState.cvProcessing.blockedReason === 'missing-data-processing-consent';

  const isAiProcessingCutoffBlocked = isCandidateAiProcessingCutoffBlocked(resolvedAiReviewState?.cvProcessing);

  const isAiSuggestionsProcessing =
    !isAiConsentBlocked &&
    !isAiProcessingCutoffBlocked &&
    (isAiRefreshSessionActive || isCandidateCvProcessingActive(resolvedAiReviewState?.cvProcessing.status));

  useStartCandidateAiRefreshForActiveProcessing({
    candidateId: data?.uuid ?? id,
    enabled: !shouldStartAiRefreshOnLoad,
    isAiRefreshSessionActive,
    refetchAiReviewState: refetchCandidateAiReviewState,
    resolvedAiReviewState,
    startCandidateAiRefreshSession,
  });

  useStartCandidateAiRefreshOnNavigation({
    candidateId: data?.uuid ?? id,
    location,
    navigate,
    refetchAiReviewState: refetchCandidateAiReviewState,
    resolvedAiReviewState,
    shouldStartAiRefreshOnLoad,
    startCandidateAiRefreshSession,
  });

  if (isLoading || !data) return <></>;

  const candidateForAiUi = resolvedCandidateWithLatestAiState ?? data;
  const aiReviewState = resolvedAiReviewState ?? getCandidateAiReviewStateFromCandidate(data);

  const handleSave = async () => {
    try {
      await form.validateFields();
    } catch {
      return;
    }

    const dataValues = form.getFieldsValue();
    updateCandidate.mutate(
      { queryParams: { id: data?.uuid ?? '' }, data: buildCandidateFormPayload(dataValues) },
      {
        onSuccess: () => clearFieldErrors(form),
        onError: (error) => {
          handleFormErrors({ error, form, mapErrorFieldsToFormLabels });
        },
      }
    );
  };

  const attachCV = (fileId: string) => {
    updateCandidate.mutate(
      { queryParams: { id: data?.uuid ?? '' }, data: { cv: fileId } },
      {
        onSuccess: () => {
          startCandidateAiRefreshSession(aiReviewState);
          void Promise.allSettled([refetch(), refetchCandidateAiReviewState()]);
        },
      }
    );
  };

  const handleSavePick = () => {
    attachCV?.(chosenAttachement);
    setChosenAttachement('');
    setPickFile(false);
  };

  const handleCancel = () => navigate(-1);

  const handleConsentChange = () => {
    void Promise.allSettled([refetch(), refetchCandidateAiReviewState()]);
  };

  const breadcrumbItems = [
    {
      title: <BreadcrumbItem path={PageRoutesEnum.main} title="Candidates" />,
    },
    {
      title: <BreadcrumbItem title={`${data.firstName} ${data.lastName}`} />,
    },
  ];

  const openAttachements = () => {
    setActive(EditCandidateTabsEnum.Attachements);
    setPickFile(true);
  };

  const closePickMode = () => {
    setPickFile(false);
    setChosenAttachement('');
  };

  const items: TabsProps['items'] = [
    {
      key: EditCandidateTabsEnum.Recruitments,
      label: `Recruitments`,
      children: <CandidateRecruitmentsTable candidateId={id ?? ''} />,
    },
    {
      key: EditCandidateTabsEnum.AdditionalData,
      label: `Additional data`,
      forceRender: true,
      children: <CandidateAdditionalDataForm form={form} />,
    },
    {
      key: EditCandidateTabsEnum.HrReview,
      label: (
        <StyledRow gap="5px">
          <div style={{ marginRight: '5px' }}> HR review</div>
          {isHrReview && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Icon name="HrReview" inlineStyle />
            </div>
          )}
        </StyledRow>
      ),
      forceRender: true,
      children: (
        <HRReview
          candidate={data}
          form={form}
          hrInterviewAttachements={hrInterviewAttachements}
          attachFile={attachHrInterviewFile}
        />
      ),
    },
    {
      key: EditCandidateTabsEnum.TechnicalReview,
      label: (
        <StyledRow gap="5px">
          <div style={{ marginRight: '5px' }}> Technical review</div>
          {isTechnicalReview && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Icon name="HrReview" inlineStyle />
            </div>
          )}
        </StyledRow>
      ),
      forceRender: true,
      children: (
        <TechnicalReview
          technicalInterviewAttachements={technicalInterviewAttachements}
          candidate={data}
          form={form}
          attachFile={attachTechnicalInterviewFile}
        />
      ),
    },
    {
      key: EditCandidateTabsEnum.Notes,
      label: `Notes`,
      children: <CandidateNotes candidateId={data?.uuid ?? ''} notes={data.notes} />,
    },
    {
      key: EditCandidateTabsEnum.Attachements,
      label: `Attachements`,
      children: (
        <CandidateAttachements
          attachments={[...otherAttachements]}
          handleAttachmentUpload={attachGeneralFile}
          handleAttachmentDragging={attachCV}
          pickMode={pickFile}
          closePickMode={closePickMode}
          onPick={(id: string) => setChosenAttachement(id)}
          onSavePick={handleSavePick}
          chosenAttachement={chosenAttachement}
        />
      ),
    },
    {
      key: EditCandidateTabsEnum.Reminders,
      label: `Reminders`,
      children: <CandidateRemindersTable candidateId={id ?? ''} />,
    },
    {
      key: EditCandidateTabsEnum.Consents,
      label: 'Consents',
      children: (
        <CandidateConsentsTable
          candidateId={id ?? ''}
          consents={data.consents}
          consentsLink={data.activeCandidateTokenLink}
          email={data.email}
          addIntent={consentsAddIntent}
          onConsentChange={handleConsentChange}
        />
      ),
    },
  ];

  const handleChangeTab = (active: string) => {
    const tabNumber = getEnumKeyByEnumValue(EditCandidateTabsEnum, active);
    if (tabNumber) {
      setActive(EditCandidateTabsEnum[tabNumber]);
    }
  };

  return (
    <CandidateDuplicatesProvider>
      <MainContainer>
        <div ref={headerRef}>
          <CandidateDetailsPageHeader
            onShowRecommendations={() => openMatchingPage(isAiReviewDirty)}
            title={<Breadcrumbs breadcrumbItems={breadcrumbItems} />}
            aiSuggestionsPendingCount={candidateForAiUi.aiSuggestions.pendingCount}
            candidateId={id ?? ''}
            isAiSuggestionsConsentBlocked={isAiConsentBlocked && candidateForAiUi.aiSuggestions.pendingCount === 0}
            isAiSuggestionsCutoffBlocked={isAiProcessingCutoffBlocked}
            isAiSuggestionsProcessing={isAiSuggestionsProcessing}
            hasUnsavedEdits={isAiReviewDirty}
            onSave={handleSave}
            onCancel={handleCancel}
          />
        </div>
        <Row gutter={[15, 15]}>
          <Col xs={24} xl={14}>
            <CandidateDataForm form={form} />
            <TabsColumn
              defaultActiveKey={EditCandidateTabsEnum.AdditionalData}
              items={items}
              onChange={handleChangeTab}
              activeKey={active}
            />
          </Col>
          <Col xs={24} xl={10}>
            <CandidatesPersonalData
              updateCVAttachement={attachCV}
              cvAttachement={data?.cv?.uuid}
              openAttachements={openAttachements}
              height={tableHeight}
            />
          </Col>
        </Row>

        <Modal
          open={isConsentModalOpen}
          title="Client Data Processing consent required"
          okText="OK"
          cancelButtonProps={{ style: { display: 'none' } }}
          onOk={() => setIsConsentModalOpen(false)}
          onCancel={() => setIsConsentModalOpen(false)}
        >
          <>
            <p>
              This candidate either does not have a Client Data Processing consent, or the existing consent is not
              approved or renewed.
            </p>
            <p>
              Please review and update the consent status on the <strong>Consents</strong> tab before sharing candidate
              data with clients.
            </p>
          </>
        </Modal>
      </MainContainer>
    </CandidateDuplicatesProvider>
  );
};
